/**
 * Global Error Boundary
 * Catches any unhandled errors and redirects to home page
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '@/shared/config';
import { useSafeThemeClasses } from '@/shared/theme';
import { logger } from '@/shared/utils';

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

// Functional component for error display (can use hooks)
const ErrorDisplay: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  const theme = useSafeThemeClasses();
  
  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${theme.background}`}>
      <div className={`${theme.card} rounded-lg shadow-lg p-8 max-w-lg w-full`}>
        <h2 className={`text-2xl font-bold mb-4 ${theme.textHeading}`}>
          Development Error
        </h2>
        <p className={`mb-4 ${theme.textSecondary}`}>
          An error occurred while rendering this page. Check the console for details.
        </p>
        <div className="flex gap-4">
          <button
            onClick={onReset}
            className={`px-4 py-2 rounded ${theme.buttonPrimary} ${theme.textPrimary}`}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = ROUTES.HOME}
            className={`px-4 py-2 rounded ${theme.buttonSecondary} ${theme.textSecondary}`}
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

class GlobalErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging in development
    if (import.meta.env.DEV) {
      console.error('GlobalErrorBoundary caught an error:', error, errorInfo);
    }

    logger.error('Unhandled render error', 'GlobalErrorBoundary', {
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      // In production, redirect to home
      // In development, show error for debugging
      if (import.meta.env.DEV) {
        return <ErrorDisplay onReset={this.handleReset} />;
      } else {
        // In production, redirect to home
        return <Navigate to={ROUTES.HOME} replace />;
      }
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
